import { useCallback, useMemo, useState } from 'react'

import type { ErrorLike } from '@apollo/client'

import { toast } from '@buffer-mono/popcorn'

import { useDismissAllMentions } from '../hooks/engagement/useDismissAllMentions'
import { useChannelMentionCounts } from '../hooks/mentions/useChannelMentionCounts'
import type { DismissActionType } from './types'
import { getOneMonthAgoIso } from '../util/dateHelpers'
import { useStableArray } from '../util/useStableArray'

/**
 * Per-channel cap for the dialog count. Matches `RESOLVE_COMMENTS_LIMIT` and the
 * backend maximum, so the number in the dialog is the real one for any plausible
 * inbox; past it the copy shows `+{limit}`.
 */
export const RESOLVE_MENTIONS_LIMIT = 1_000

type UseResolveMentionsDialogParams = {
  channelIds: string[]
  actionType: DismissActionType
  shouldOpen: boolean
  onOpenChange?: (open: boolean) => void
  onHideTooltip?: () => void
  onResolvingChange?: (isResolving: boolean) => void
}

/**
 * Orchestrates the mentions bulk-resolve confirmation dialog: counts the
 * mentions the action would resolve, then runs the bulk dismiss. Mirrors
 * `useResolveCommentsDialog`, minus the post-scoped action, which mentions do
 * not offer.
 */
export const useResolveMentionsDialog = ({
  channelIds,
  actionType,
  shouldOpen,
  onOpenChange,
  onHideTooltip,
  onResolvingChange,
}: UseResolveMentionsDialogParams): {
  isOpen: boolean
  handleOpenChange: (open: boolean) => void
  handleResolveMentions: () => Promise<void>
  isResolving: boolean
  mentionCount: number | null
  isLoadingCount: boolean
  errorCount: ErrorLike | null
  effectiveLimit: number | null
} => {
  const [isResolving, setIsResolving] = useState(false)

  const { dismissAllMentions } = useDismissAllMentions()

  // Stabilize channelIds array reference to keep the callbacks stable.
  const stableChannelIds = useStableArray(channelIds)

  const channels = useMemo(
    () => stableChannelIds.map((id) => ({ id })),
    [stableChannelIds],
  )

  // One cutoff per opening of the dialog. The count and the dismiss both use it,
  // so the number the user confirms is the number the mutation acts on, and a
  // cutoff that moved every render would be a new query every render.
  const cutoffIso = useMemo(
    () => (actionType === 'older-than-month' ? getOneMonthAgoIso() : undefined),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [actionType, shouldOpen],
  )

  const {
    counts,
    loading: isLoadingCount,
    error: errorCount,
  } = useChannelMentionCounts({
    channels,
    limit: RESOLVE_MENTIONS_LIMIT,
    publishedAt: cutoffIso ? { end: cutoffIso } : undefined,
    skip: !shouldOpen,
  })

  const handleOpenChange = useCallback(
    (open: boolean): void => {
      onOpenChange?.(open)
      if (!open) {
        onHideTooltip?.()
      }
    },
    [onOpenChange, onHideTooltip],
  )

  const handleResolveMentions = useCallback(async (): Promise<void> => {
    setIsResolving(true)
    onResolvingChange?.(true)
    try {
      await dismissAllMentions(stableChannelIds, cutoffIso)
      onOpenChange?.(false)
      onHideTooltip?.()
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Unknown error'
      toast.error(message)
    } finally {
      setIsResolving(false)
      onResolvingChange?.(false)
    }
  }, [
    dismissAllMentions,
    stableChannelIds,
    cutoffIso,
    onOpenChange,
    onHideTooltip,
    onResolvingChange,
  ])

  return {
    isOpen: shouldOpen,
    handleOpenChange,
    handleResolveMentions,
    isResolving,

    mentionCount: counts
      ? counts.reduce((sum, channel) => sum + channel.count, 0)
      : null,
    isLoadingCount,
    errorCount,
    effectiveLimit: counts?.[0]?.limit ?? null,
  }
}
